import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  FaArrowRight,
  FaBriefcase,
  FaCalendarCheck,
  FaCheckCircle,
  FaHeadset,
  FaPlaneDeparture,
  FaRoute,
  FaSuitcaseRolling,
} from "react-icons/fa";
import FlightSearchWidget from "../helpers/FlightSearchWidget";
import { airlines, findAirlineBySlug } from "../../data/airlines";

const buildHeadings = (airline) => [
  `Why book ${airline.name} flights with CheapOflyHub?`,
  `How to book ${airline.name} flights`,
  `Manage your ${airline.name} booking`,
  "Seat selection and upgrades",
  "Flight change policy",
  "Flight cancellation policy",
  "Baggage policies",
  "Check-in options",
  "Popular routes",
];

const popularRoutes = [
  "New York - London",
  "Miami - Dubai",
  "Los Angeles - Tokyo",
  "Chicago - Cancun",
  "San Francisco - Delhi",
  "Houston - Mexico City",
];

export default function AirlineDetail() {
  const { airlineSlug } = useParams();
  const airline = findAirlineBySlug(airlineSlug);
  const [activeSection, setActiveSection] = useState(1);

  const headings = useMemo(
    () => (airline ? buildHeadings(airline) : []),
    [airline],
  );
  const otherAirlines = airline
    ? airlines.filter((item) => item.slug !== airline.slug).slice(0, 8)
    : [];

  useEffect(() => {
    const handleScroll = () => {
      const currentSection = headings.reduce((current, _heading, index) => {
        const section = document.getElementById(`section-${index + 1}`);

        if (!section) {
          return current;
        }

        const sectionTop = section.getBoundingClientRect().top;
        return sectionTop <= 180 ? index + 1 : current;
      }, 1);

      setActiveSection(currentSection);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [headings]);

  if (!airline) {
    return <Navigate to="/flights" replace />;
  }

  return (
    <div className="bg-[#f8f9fc] font-sans">
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(40,113,211,0.45),transparent_36%),linear-gradient(135deg,#0f172a,#1d4ed8)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm font-bold text-blue-100">
            <Link to="/" className="transition hover:text-white">
              Home
            </Link>
            <FaArrowRight className="h-3 w-3" />
            <Link to="/flights" className="transition hover:text-white">
              Airlines
            </Link>
            <FaArrowRight className="h-3 w-3" />
            <span className="text-white">{airline.name}</span>
          </nav>

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-end">
            <div>
              <p className="mb-4 inline-flex rounded-full bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-blue-100 ring-1 ring-white/15">
                Airline guide
              </p>
              <h1 className="max-w-4xl text-4xl font-black leading-tight tracking-tight sm:text-6xl">
                Book {airline.name} Flights
              </h1>
              <p className="mt-5 max-w-3xl text-base font-medium leading-8 text-blue-50 sm:text-lg">
                {airline.summary} Search live fares, compare routes, and get
                booking support for your next trip.
              </p>
            </div>
            <div className="rounded-[1.5rem] border border-white/15 bg-white/10 p-6 backdrop-blur-xl">
              <div className="text-sm font-bold uppercase tracking-[0.18em] text-blue-100">
                Airline code
              </div>
              <div className="mt-2 text-5xl font-black">{airline.code}</div>
              <div className="mt-4 rounded-full bg-white px-4 py-2 text-sm font-black text-[#2871d3]">
                {airline.region}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FlightSearchWidget />

      <section className="mx-auto grid max-w-7xl gap-6 px-4 py-12 sm:px-6 lg:grid-cols-[260px_minmax(0,1fr)_280px] lg:px-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-[0_16px_45px_rgba(15,23,42,0.06)]">
            <h2 className="mb-4 text-sm font-black uppercase tracking-[0.18em] text-slate-400">
              Contents
            </h2>
            <div className="space-y-2">
              {headings.map((heading, index) => (
                <a
                  key={heading}
                  href={`#section-${index + 1}`}
                  onClick={() => setActiveSection(index + 1)}
                  className={`block rounded-xl px-3 py-2 text-sm font-bold transition ${
                    activeSection === index + 1
                      ? "bg-blue-50 text-[#2871d3] shadow-sm ring-1 ring-blue-100"
                      : "text-slate-600 hover:bg-blue-50 hover:text-[#2871d3]"
                  }`}
                >
                  {heading}
                </a>
              ))}
            </div>
          </div>
        </aside>

        <main className="min-w-0 space-y-6">
          <article className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)] sm:p-8">
            <h2 className="text-3xl font-black tracking-tight text-slate-950">
              {airline.name} flight booking guide
            </h2>
            <p className="mt-4 text-base font-medium leading-8 text-slate-600">
              {airline.name} flights can be searched directly on CheapOflyHub.
              Choose your origin, destination, dates, cabin class, and trip
              type, then compare available fares in one place. Our booking
              flow is designed for quick comparison, transparent pricing, and
              support when plans change.
            </p>
          </article>

          {headings.map((heading, index) => (
            <section
              key={heading}
              id={`section-${index + 1}`}
              className={`scroll-mt-28 rounded-[1.5rem] border bg-white p-6 shadow-sm transition-all duration-300 sm:p-8 ${
                activeSection === index + 1
                  ? "border-blue-200 ring-4 ring-blue-50"
                  : "border-slate-200"
              }`}
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-[#2871d3]">
                {index % 4 === 0 && <FaPlaneDeparture />}
                {index % 4 === 1 && <FaCalendarCheck />}
                {index % 4 === 2 && <FaHeadset />}
                {index % 4 === 3 && <FaBriefcase />}
              </div>
              <h2 className="text-2xl font-black tracking-tight text-slate-950">
                {heading}
              </h2>
              <p className="mt-4 text-sm font-medium leading-7 text-slate-600 sm:text-base">
                CheapOflyHub helps travelers compare {airline.name} options
                alongside other carriers. Review departure times, stops, cabin
                options, baggage details, and fare conditions before choosing a
                flight. For urgent help, call our travel advisors for live
                support and fare guidance.
              </p>
              {heading === "Popular routes" && (
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {popularRoutes.map((route) => (
                    <div
                      key={route}
                      className="flex items-center gap-3 rounded-2xl bg-[#f8f9fc] p-4 text-sm font-black text-slate-700"
                    >
                      <FaRoute className="text-[#2871d3]" />
                      {route}
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}

          <section className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="text-2xl font-black tracking-tight text-slate-950">
              Quick answers
            </h2>
            <div className="mt-5 space-y-3">
              {[
                `How do I find cheap ${airline.name} flights?`,
                `Can I change my ${airline.name} booking?`,
                `Are baggage fees included?`,
              ].map((question) => (
                <details
                  key={question}
                  className="rounded-2xl border border-slate-200 bg-[#f8f9fc] p-4"
                >
                  <summary className="cursor-pointer text-sm font-black text-slate-950">
                    {question}
                  </summary>
                  <p className="mt-3 text-sm font-medium leading-6 text-slate-600">
                    Use the search form on this page to compare live fares and
                    review the fare details shown in the results. Rules vary by
                    ticket and airline, so check the selected offer before
                    booking.
                  </p>
                </details>
              ))}
            </div>
          </section>
        </main>

        <aside className="space-y-5">
          <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)]">
            <h2 className="text-lg font-black text-slate-950">
              Need help booking?
            </h2>
            <p className="mt-2 text-sm font-medium leading-6 text-slate-500">
              Speak with a travel advisor for route and fare assistance.
            </p>
            <a
              href="tel:+18883385981"
              className="mt-5 flex w-full items-center justify-center rounded-full bg-[#2871d3] px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:bg-[#155bc2]"
            >
              Call 1-888-338-5981
            </a>
          </div>

          <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-lg font-black text-slate-950">
              Other popular airlines
            </h2>
            <div className="space-y-2">
              {otherAirlines.map((item) => (
                <Link
                  key={item.slug}
                  to={`/flights/${item.slug}`}
                  className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-[#f8f9fc] p-3 transition hover:border-blue-100 hover:bg-blue-50"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-sm font-black text-[#2871d3]">
                    {item.code}
                  </span>
                  <span className="text-sm font-black text-slate-700">
                    {item.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <FaSuitcaseRolling className="text-[#2871d3]" />
              <span className="text-sm font-black text-slate-950">
                Baggage and fare rules vary by ticket.
              </span>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <FaCheckCircle className="text-emerald-500" />
              <span className="text-sm font-black text-slate-950">
                Review conditions before checkout.
              </span>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
